import { useCallback, useMemo, type MouseEvent } from 'react'
import {
  ChevronRightIcon,
  ExternalLinkIcon,
  LinkIcon,
  MoreHorizontal,
  PencilIcon,
  TrashIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import {
  type RiskReductionMeasure,
  type SafetyFunction,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item'
import { PlrCircleBadge } from '../../components/PlrCircleBadge'
import {
  getLinkedSafetyFunctionDisplay,
  isSafetyFunctionMeasure,
} from './riskReductionMeasuresUtils'

function getMeasureTypeLabel(
  measureType: RiskReductionMeasure['type'],
  translate: (key: string) => string
) {
  return translate(
    `riskReductionMeasuresLibraryPage.form.measureTypes.${measureType}`
  )
}

export function RiskReductionMeasureCard({
  riskReductionMeasure,
  safetyFunctions,
  onEdit,
  onDelete,
  onAssign,
  isMutationPending,
}: {
  riskReductionMeasure: RiskReductionMeasure
  safetyFunctions: SafetyFunction[]
  onEdit: (riskReductionMeasure: RiskReductionMeasure) => void
  onDelete: (riskReductionMeasureId: string) => void
  onAssign: (riskReductionMeasure: RiskReductionMeasure) => void
  isMutationPending: boolean
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const navigate = useNavigate()

  const handleEdit = useCallback(() => {
    onEdit(riskReductionMeasure)
  }, [onEdit, riskReductionMeasure])

  const handleDelete = useCallback(() => {
    onDelete(riskReductionMeasure.id)
  }, [onDelete, riskReductionMeasure.id])

  const handleAssign = useCallback(() => {
    onAssign(riskReductionMeasure)
  }, [onAssign, riskReductionMeasure])

  const isEligibleForPlr = isSafetyFunctionMeasure(riskReductionMeasure)
  const hasLinkedSafetyFunction =
    riskReductionMeasure.safetyFunctionId != null &&
    riskReductionMeasure.safetyFunctionId.trim() !== ''

  const linkedSafetyFunctionDisplay = useMemo(
    () =>
      getLinkedSafetyFunctionDisplay(
        riskReductionMeasure.safetyFunctionId,
        safetyFunctions
      ),
    [riskReductionMeasure.safetyFunctionId, safetyFunctions]
  )

  const openDetail = () => {
    navigate(riskReductionMeasure.id)
  }

  const stopNestedClick = (event: MouseEvent) => {
    event.stopPropagation()
  }

  const isSourcedFromStandard = riskReductionMeasure.source === 'STANDARD'
  const safetyFunctionButtonLabel = hasLinkedSafetyFunction
    ? t('riskReductionMeasuresLibraryPage.actions.changeSafetyFunction')
    : t('riskReductionMeasuresLibraryPage.actions.assignSafetyFunction')
  const showLinkedSafetyFunction =
    hasLinkedSafetyFunction && linkedSafetyFunctionDisplay != null
  return (
    <Card
      className='cursor-pointer transition-colors hover:border-primary/50'
      onClick={openDetail}
    >
      <CardHeader className='flex flex-col md:flex-row md:items-center md:justify-between'>
        <CardTitle className='flex flex-col gap-2 md:flex-row md:items-center'>
          <span>{riskReductionMeasure.name}</span>

          <Badge
            variant='outline'
            className='font-normal text-muted-foreground'
          >
            {getMeasureTypeLabel(riskReductionMeasure.type, t)}
          </Badge>
        </CardTitle>
        <div className='flex items-center gap-2'>
          {isSourcedFromStandard && (
            <Badge
              variant='outline'
              className='font-normal text-muted-foreground'
            >
              {t('riskReductionMeasuresLibraryPage.badges.fromStandards')}
            </Badge>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={stopNestedClick}>
              <Button
                variant='ghost'
                size='icon'
                className='size-8'
                disabled={isMutationPending}
              >
                <MoreHorizontal className='size-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' onClick={stopNestedClick}>
              <DropdownMenuItem onClick={openDetail}>
                {t('riskReductionMeasuresLibraryPage.actions.view')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleEdit}>
                <PencilIcon />
                {t('riskReductionMeasuresLibraryPage.actions.edit')}
              </DropdownMenuItem>
              {isEligibleForPlr && (
                <DropdownMenuItem onClick={handleAssign}>
                  <LinkIcon />
                  {safetyFunctionButtonLabel}
                </DropdownMenuItem>
              )}
              {hasLinkedSafetyFunction && (
                <DropdownMenuItem asChild>
                  <Link
                    to={`../safety-functions/${riskReductionMeasure.safetyFunctionId}`}
                    relative='route'
                  >
                    <ExternalLinkIcon />
                    {t(
                      'riskReductionMeasuresLibraryPage.actions.manageSafetyFunction'
                    )}
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant='destructive'
                onClick={handleDelete}
                className='text-destructive focus:text-destructive'
              >
                <TrashIcon />
                {t('riskReductionMeasuresLibraryPage.actions.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className='space-y-2'>
        <div className='flex gap-4'>
          <div className='flex-1'>
            <div className='flex flex-col justify-between gap-2 md:flex-row'>
              <p className='text-sm text-muted-foreground'>
                {riskReductionMeasure.description || '-'}
              </p>

              {isEligibleForPlr && (
                <div className='flex items-center justify-center gap-2 md:flex-col md:gap-0'>
                  <div className='mb-1 text-center text-sm font-semibold text-muted-foreground'>
                    PLr
                  </div>
                  <PlrCircleBadge plr={riskReductionMeasure.plr} />
                </div>
              )}
            </div>

            {showLinkedSafetyFunction && (
              <div className='mt-4' onClick={stopNestedClick}>
                <Item
                  variant='outline'
                  size='sm'
                  render={
                    <Link
                      to={`../safety-functions/${riskReductionMeasure.safetyFunctionId}`}
                      relative='route'
                    />
                  }
                >
                  <ItemContent>
                    <ItemTitle className='text-sm'>
                      {t(
                        'riskReductionMeasuresLibraryPage.list.linkedSafetyFunctionLabel'
                      )}
                    </ItemTitle>
                    <ItemDescription className='text-xs'>
                      {linkedSafetyFunctionDisplay.name}
                      {linkedSafetyFunctionDisplay.eplanReferenceId != null ? (
                        <span className='block text-muted-foreground'>
                          {t('safetyFunctionsPage.card.eplanReferenceId', {
                            referenceId:
                              linkedSafetyFunctionDisplay.eplanReferenceId,
                          })}
                        </span>
                      ) : null}
                    </ItemDescription>
                  </ItemContent>
                  <ItemActions>
                    <ChevronRightIcon className='size-4' />
                  </ItemActions>
                </Item>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
